'use client';

import { STORAGE_KEY } from '@/constants';
import { useAuth } from '@/contexts/AuthProvider';
import { useLocalStorage } from '@/hooks';
import {
  useGetActiveWorkspace,
  useGetWorkspaces,
  useSwitchWorkspace,
} from '@/hooks/useWorkspaceQuery';
import { IWorkspaceListItem, IWorkspaceWithMembership } from '@/types/workspace';
import { handleShowError } from '@/utils';
import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter, usePathname } from 'next/navigation';

interface WorkspaceProviderProps {
  children: ReactNode;
}

interface WorkspaceContextValue {
  // State
  workspaces: IWorkspaceListItem[];
  activeWorkspace: IWorkspaceWithMembership | null;
  isLoading: boolean;
  isCreateModalOpen: boolean;
  isSwitchingWorkspace: boolean;

  // Error states
  isError: boolean;
  error: Error | null;

  // Actions
  switchWorkspace: (workspaceId: number) => Promise<void>;
  refetchWorkspaces: () => void;
  refetchActiveWorkspace: () => void;
  setIsCreateModalOpen: (isOpen: boolean) => void;

  // Computed
  hasWorkspaces: boolean;
  isAdmin: boolean;
  canUsePaidCredits: boolean;
  isEnterprise: boolean;
  // NEW: Enhanced tier checking
  subscriptionTier: string | null;
  isProfessionalOrHigher: boolean;
  hasAccessToTier: (allowedTiers: string[]) => boolean;
}

const WorkspaceContext = createContext<WorkspaceContextValue | undefined>(undefined);

// Add active workspace ID to storage keys
export const WORKSPACE_STORAGE_KEY = 'activeWorkspaceId';

export const WorkspaceProvider: FC<WorkspaceProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const [activeWorkspaceId, setActiveWorkspaceId] = useLocalStorage(WORKSPACE_STORAGE_KEY, null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [isSwitchingWorkspace, setIsSwitchingWorkspace] = useState(false);

  // Handle hydration
  useEffect(() => {
    setIsHydrated(true);

    // Clean up invalid workspace ID from localStorage
    const storedWorkspaceId = localStorage.getItem(WORKSPACE_STORAGE_KEY);
    if (storedWorkspaceId === 'null' || storedWorkspaceId === 'undefined') {
      localStorage.removeItem(WORKSPACE_STORAGE_KEY);
    }
  }, []);

  // CRITICAL FIX: Only run queries when both authenticated AND hydrated
  // This prevents race conditions during initial page load
  const queriesEnabled = isAuthenticated && isHydrated;
  
  // Queries - only run when authenticated and hydrated
  const {
    data: workspacesData,
    isLoading: isLoadingWorkspaces,
    isError: isErrorWorkspaces,
    error: workspacesError,
    refetch: refetchWorkspaces,
  } = useGetWorkspaces(queriesEnabled);

  const {
    data: activeWorkspaceData,
    isLoading: isLoadingActiveWorkspace,
    isError: isErrorActiveWorkspace,
    error: activeWorkspaceError,
    refetch: refetchActiveWorkspace,
  } = useGetActiveWorkspace(queriesEnabled);

  // Mutations
  const { mutateAsync: switchWorkspaceMutation } = useSwitchWorkspace();
  const queryClient = useQueryClient();

  // Extract data
  const workspaces = workspacesData?.workspaces || [];
  const activeWorkspace = activeWorkspaceData?.activeWorkspace || null;

  // REMOVED: Automatic localStorage update to fix race condition
  // localStorage is now only updated manually in switchWorkspace function after API success

  // Set initial workspace ID from activeWorkspace data
  useEffect(() => {
    if (activeWorkspace?.vendorid && !activeWorkspaceId) {
      setActiveWorkspaceId(activeWorkspace.vendorid);
    }
  }, [activeWorkspace?.vendorid, activeWorkspaceId, setActiveWorkspaceId]);

  // Clear workspace data when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      setActiveWorkspaceId(null);
    }
  }, [isAuthenticated, setActiveWorkspaceId]);

  // Workspace switch notification system
  const notifyWorkspaceSwitch = useCallback(
    (newWorkspaceId: number, oldWorkspaceId: number | null) => {
      window.dispatchEvent(
        new CustomEvent('workspaceSwitch', {
          detail: { newWorkspaceId, oldWorkspaceId },
        }),
      );
    },
    [],
  );

  // Switch workspace function
  const switchWorkspace = useCallback(
    async (workspaceId: number) => {
      console.log('🔄 WorkspaceProvider: switchWorkspace called', {
        targetWorkspaceId: workspaceId,
        currentWorkspaceId: activeWorkspaceId,
        isSwitchingWorkspace
      });

      // Guard against concurrent switches
      if (isSwitchingWorkspace) {
        console.warn('⚠️ WorkspaceProvider: Workspace switch already in progress');
        return;
      }

      const previousWorkspaceId = activeWorkspaceId;

      try {
        setIsSwitchingWorkspace(true);

        // Step 1: API call FIRST - ensure backend accepts the switch
        await switchWorkspaceMutation({ workspaceId });

        // Step 2: Immediately update localStorage after confirmed success
        setActiveWorkspaceId(workspaceId);
        
        // Step 2.5: Force immediate localStorage sync for other components
        localStorage.setItem(WORKSPACE_STORAGE_KEY, String(workspaceId));

        // Step 3: Notify other systems of workspace change
        notifyWorkspaceSwitch(workspaceId, previousWorkspaceId);

        // Step 4: Clear ALL queries first to force fresh data
        queryClient.clear();

        // Step 5: Invalidate and refetch critical queries in sequence
        // First, get the new active workspace data
        await queryClient.invalidateQueries({ queryKey: ['activeWorkspace'] });
        await refetchActiveWorkspace();
        
        // Then get the workspace list
        await queryClient.invalidateQueries({ queryKey: ['workspaces'] });
        await refetchWorkspaces();

        // Step 6: Invalidate all other workspace-dependent queries
        await Promise.all([
          // Invalidate bot queries (they depend on workspace/vendor ID)
          queryClient.invalidateQueries({ queryKey: ['getAllBotsData'] }),
          queryClient.invalidateQueries({ queryKey: ['getVendorId'] }),

          // Invalidate dashboard queries
          queryClient.invalidateQueries({ queryKey: ['getDashboardScore'] }),
          queryClient.invalidateQueries({ queryKey: ['getThreadDetails'] }),
          queryClient.invalidateQueries({ queryKey: ['getTrafficAnalytics'] }),

          // Invalidate audience and campaign queries
          queryClient.invalidateQueries({ queryKey: ['audiences'] }),
          queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
          queryClient.invalidateQueries({ queryKey: ['plays'] }),
          
          // Invalidate subscription queries
          queryClient.invalidateQueries({ queryKey: ['subscriptions'] }),
          queryClient.invalidateQueries({ queryKey: ['workspace-subscriptions'] }),
        ]);
        
        // Step 7: Force a small delay to ensure state propagation
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Success notification
        const targetWorkspace = workspaces.find(w => w.vendorid === workspaceId);
        if (targetWorkspace) {
          toast.success(`Switched to ${targetWorkspace.workspace_name}`);
        }
      } catch (error) {
        // localStorage remains unchanged on failure
        handleShowError(error);
        toast.error('Failed to switch workspace. Please try again.');

        // Ensure UI reflects the current valid state
        await refetchActiveWorkspace();
        throw error;
      } finally {
        setIsSwitchingWorkspace(false);
      }
    },
    [
      isSwitchingWorkspace,
      activeWorkspaceId,
      switchWorkspaceMutation,
      setActiveWorkspaceId,
      notifyWorkspaceSwitch,
      queryClient,
      refetchActiveWorkspace,
      refetchWorkspaces,
    ],
  );

  // Workspace sync event listeners for backend/frontend synchronization
  useEffect(() => {
    const handleWorkspaceSyncMismatch = async (event: Event) => {
      const customEvent = event as CustomEvent;
      const { detail } = customEvent;
      console.log('🔄 Handling workspace sync event:', detail);

      try {
        switch (detail.type) {
          case 'sync_to_server':
            // Backend is authoritative, sync frontend state
            if (detail.correctedId && detail.correctedId !== activeWorkspaceId?.toString()) {
              console.log('🔄 Syncing to server workspace:', detail.correctedId);

              // Update internal state to match server
              setActiveWorkspaceId(parseInt(detail.correctedId));

              // Invalidate queries to fetch fresh data
              await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['workspaces'] }),
                queryClient.invalidateQueries({ queryKey: ['activeWorkspace'] }),
              ]);

              // Notify user about the sync - commented out to reduce noise
              // toast.info(`Workspace synchronized (${detail.context}) - Your workspace has been updated to match the server`);
            }
            break;

          case 'validation_needed':
            // Both frontend and backend need validation
            console.log('🔄 Workspace validation needed');

            // Force refetch of active workspace to resolve discrepancy
            await refetchActiveWorkspace();

            // Show user notification about sync validation - commented out to reduce noise
            // toast.warning('Workspace state validated - Workspace information has been refreshed');
            break;

          default:
            console.warn('Unknown workspace sync event type:', detail.type);
        }
      } catch (error) {
        console.error('Error handling workspace sync event:', error);
        // toast.error('Failed to synchronize workspace state');
      }
    };

    const handleWorkspaceSyncCorrected = async (event: Event) => {
      const customEvent = event as CustomEvent;
      const { detail } = customEvent;
      console.log('🔄 Workspace automatically corrected:', detail);

      // Update internal state to match corrected value
      if (detail.correctedId && detail.correctedId !== activeWorkspaceId?.toString()) {
        setActiveWorkspaceId(parseInt(detail.correctedId));

        // Refresh workspace data
        await Promise.all([refetchActiveWorkspace(), refetchWorkspaces()]);
      }
    };

    // Add event listeners
    window.addEventListener(
      'workspace-sync-mismatch',
      handleWorkspaceSyncMismatch as EventListener,
    );
    window.addEventListener(
      'workspace-sync-corrected',
      handleWorkspaceSyncCorrected as EventListener,
    );

    return () => {
      window.removeEventListener(
        'workspace-sync-mismatch',
        handleWorkspaceSyncMismatch as EventListener,
      );
      window.removeEventListener(
        'workspace-sync-corrected',
        handleWorkspaceSyncCorrected as EventListener,
      );
    };
  }, [
    activeWorkspaceId,
    queryClient,
    refetchActiveWorkspace,
    refetchWorkspaces,
    setActiveWorkspaceId,
  ]);

  // Computed values
  const hasWorkspaces = workspaces.length > 0;
  const isAdmin = activeWorkspace?.user_membership?.is_admin || false;
  const canUsePaidCredits = activeWorkspace?.user_membership?.can_use_paid_credits || false;
  const isLoading = isLoadingWorkspaces || isLoadingActiveWorkspace;
  const isError = isErrorWorkspaces || isErrorActiveWorkspace;
  const error = workspacesError || activeWorkspaceError;

  /**
   * WORKSPACE FEATURE ROLLBACK: Disabled automatic workspace onboarding redirect
   *
   * BEFORE (workspace feature enabled):
   * Automatically redirected users without workspaces to workspace-intent onboarding flow
   *
   * AFTER (workspace feature disabled):
   * Users without workspaces can access dashboard and other pages normally
   * Dashboard shows NoWorkspaceMessage with appropriate messaging
   *
   * TO RE-ENABLE WORKSPACE FEATURE:
   * 1. Uncomment the useEffect below
   * 2. This will restore automatic redirection to workspace onboarding for new users
   * 3. Users without workspaces will be guided through workspace creation/joining
   *
   * @see /components/NoWorkspaceMessage/index.tsx - Modified to remove action buttons
   * @see /views/dashboard/index.tsx - Shows NoWorkspaceMessage when no workspaces
   */

  // WORKSPACE ROLLBACK: Force new user onboarding disabled
  // useEffect(() => {
  //   if (!isLoading && !hasWorkspaces && isAuthenticated && isHydrated) {
  //     // Don't redirect if already on workspace intent pages
  //     const isOnWorkspaceFlow =
  //       pathname?.includes('/onboarding/workspace-intent') ||
  //       pathname?.includes('/onboarding/create-workspace') ||
  //       pathname?.includes('/onboarding/join-workspace');

  //     // Don't redirect if on settings page (personal settings don't require workspace)
  //     const isOnSettingsPage = pathname?.includes('/settings');

  //     if (!isOnWorkspaceFlow && !isOnSettingsPage) {
  //       router.push('/onboarding/workspace-intent');
  //     }
  //   }
  // }, [isLoading, hasWorkspaces, isAuthenticated, isHydrated, pathname, router]);

  // Context value
  // Check if workspace is enterprise (legacy)
  const isEnterprise = useMemo(() => {
    return activeWorkspace?.subscriptionplan === 'enterprise';
  }, [activeWorkspace?.subscriptionplan]);

  // NEW: Enhanced tier checking
  const subscriptionTier = activeWorkspace?.subscriptionplan || null;
  
  const isProfessionalOrHigher = useMemo(() => {
    const tier = activeWorkspace?.subscriptionplan;
    return tier === 'professional' || tier === 'pro' || tier === 'enterprise';
  }, [activeWorkspace?.subscriptionplan]);

  const hasAccessToTier = useCallback((allowedTiers: string[]) => {
    const currentTier = activeWorkspace?.subscriptionplan;
    if (!currentTier || !allowedTiers.length) return false;
    
    // Direct match
    if (allowedTiers.includes(currentTier)) return true;
    
    // Legacy plan mapping
    const legacyMappings = {
      'starter': ['kickstart'],
      'professional': ['growth', 'pro'], 
      'kickstart': ['starter'],
      'growth': ['professional'],
      'pro': ['professional'],
    };
    
    // Check if current tier maps to any allowed tier
    const mappedTiers = legacyMappings[currentTier as keyof typeof legacyMappings] || [];
    return mappedTiers.some(mapped => allowedTiers.includes(mapped));
  }, [activeWorkspace?.subscriptionplan]);

  const contextValue = useMemo<WorkspaceContextValue>(
    () => ({
      // State
      workspaces,
      activeWorkspace,
      isLoading,
      isCreateModalOpen,
      isSwitchingWorkspace,

      // Error states
      isError,
      error,

      // Actions
      switchWorkspace,
      refetchWorkspaces,
      refetchActiveWorkspace,
      setIsCreateModalOpen,

      // Computed
      hasWorkspaces,
      isAdmin,
      canUsePaidCredits,
      isEnterprise,
      // NEW: Enhanced tier checking
      subscriptionTier,
      isProfessionalOrHigher,
      hasAccessToTier,
    }),
    [
      workspaces,
      activeWorkspace,
      isLoading,
      isCreateModalOpen,
      isSwitchingWorkspace,
      isError,
      error,
      hasWorkspaces,
      isAdmin,
      canUsePaidCredits,
      isEnterprise,
      subscriptionTier,
      isProfessionalOrHigher,
      hasAccessToTier,
      switchWorkspace,
      refetchWorkspaces,
      refetchActiveWorkspace,
    ],
  );

  // Don't provide context when not authenticated
  if (!isAuthenticated) {
    return <>{children}</>;
  }

  // Prevent hydration mismatch by not rendering context until client-side hydration
  if (!isHydrated) {
    return <>{children}</>;
  }

  return <WorkspaceContext.Provider value={contextValue}>{children}</WorkspaceContext.Provider>;
};

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);

  // Allow usage outside of provider (returns undefined)
  // This is useful for components that might render before auth
  return context;
};

// Hook that requires workspace context
export const useWorkspaceContext = () => {
  const context = useContext(WorkspaceContext);

  if (!context) {
    throw new Error('useWorkspaceContext must be used within WorkspaceProvider');
  }

  return context;
};
